/**
 * Calculs d'altitude déportés hors du fil principal.
 *
 * Les deux opérations portées ici parcourent tous les points d'une trace, soit
 * jusqu'à deux cent mille. Le report des altitudes sur un itinéraire routé est
 * le plus lourd : il apparie chaque point d'origine dans une fenêtre de
 * plusieurs centaines de points routés. Sur le fil principal, la carte se fige
 * plusieurs secondes — assez pour qu'on croie l'application plantée.
 *
 * Le repli sur le fil principal, lui, vit dans `workerBridge.ts` : il est le
 * même ici et pour l'analyse GPX.
 */

import { interpolateElevations } from './routeElevation'
import { cumulativeDistances, interpolateSamples } from './elevationFetch'
import { runInWorker } from './workerBridge'
import type { TrackPoint } from './types'

export type WorkerRequest =
  | {
      readonly kind: 'route-elevations'
      readonly routePoints: readonly TrackPoint[]
      readonly originalPoints: readonly TrackPoint[]
    }
  | {
      readonly kind: 'sample-elevations'
      readonly points: readonly TrackPoint[]
      readonly sampleIdx: readonly number[]
      readonly sampleEle: readonly number[]
    }

/** Exécute une requête, quel que soit le fil sur lequel on se trouve. */
export function runElevationTask(request: WorkerRequest): TrackPoint[] {
  if (request.kind === 'route-elevations') {
    return interpolateElevations(request.routePoints, request.originalPoints)
  }

  const cumulative = cumulativeDistances(request.points)
  const elevations = interpolateSamples(
    cumulative,
    request.sampleIdx,
    request.sampleEle,
  )

  return request.points.map((point, i) => ({ ...point, ele: elevations[i]! }))
}

/** Confie un calcul à un travailleur, avec repli sur le fil principal. */
export function runElevationTaskAsync(
  request: WorkerRequest,
): Promise<TrackPoint[]> {
  return runInWorker(
    () =>
      new Worker(new URL('./elevationWorker.entry.ts', import.meta.url), {
        type: 'module',
      }),
    request,
    runElevationTask,
  )
}
